import {
  model,
  Model,
  prop,
  idProp,
  rootRef,
  detach,
  modelAction,
  findParent,
  modelFlow,
  _async,
} from "mobx-keystone";
import { Bob } from "./Bob";
import { sendToBobController } from "../../helpers/requests";
import { BOB_MODALS, TIKTOK_SPONSORSHIPS_ENABLED } from "../helpers/constants";

const VALID_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const CODE_LENGTH = 9; // 4 chars + dash + 4 chars

@model("exrails/BobSponsorshipProposal")
export class BobSponsorshipProposal extends Model({
  _version: prop(0),
  id: idProp,
  proposedCode: prop<string>(""),
  userFacingCodeErrorMessage: prop<string | null>(null),
  validCode: prop<string | null>(null),
  serverErrorMessage: prop<string | null>(null),
  serverSuccessMessage: prop<string | null>(null),
}) {
  @modelAction
  setProposedCode(raw: string) {
    // Allow clearing
    if (raw === "") {
      this.proposedCode = "";
      this.userFacingCodeErrorMessage = null;
      this.validCode = null;
      this.serverErrorMessage = null;
      this.serverSuccessMessage = null;
      return;
    }

    // Strip whitespace and uppercase
    let cleaned = raw.replace(/\s/g, "").toUpperCase();
    // Strip dashes to work with raw chars
    const noDash = cleaned.replace(/-/g, "");

    // Check for invalid characters
    for (const ch of noDash) {
      if (!VALID_CHARS.includes(ch)) {
        if ("IO01".includes(ch)) {
          this.userFacingCodeErrorMessage = `"${ch}" is not used in promo codes. Did you mean a different character?`;
        } else {
          this.userFacingCodeErrorMessage = `"${ch}" is not a valid promo code character.`;
        }
        this.validCode = null;
        return;
      }
    }

    // Limit to 8 valid chars
    const chars = noDash.slice(0, 8);
    // Re-insert dash after 4th char
    const formatted =
      chars.length > 4 ? chars.slice(0, 4) + "-" + chars.slice(4) : chars;

    this.proposedCode = formatted;
    this.userFacingCodeErrorMessage = null;
    this.serverErrorMessage = null;
    this.serverSuccessMessage = null;

    // Check if complete
    if (formatted.length === CODE_LENGTH) {
      const parts = formatted.split("-");
      if (
        parts.length === 2 &&
        parts[0].length === 4 &&
        parts[1].length === 4 &&
        [...parts[0], ...parts[1]].every((ch) => VALID_CHARS.includes(ch))
      ) {
        this.validCode = formatted;
      } else {
        this.validCode = null;
      }
    } else {
      this.validCode = null;
    }
  }

  get isCodeReady(): boolean {
    return this.validCode !== null;
  }

  get charsRemaining(): number {
    return CODE_LENGTH - this.proposedCode.length;
  }

  @modelFlow
  checkPromoCode = _async(function* (this: BobSponsorshipProposal) {
    this.bob.setAreWeWaitingOnRails(true);
    this.serverErrorMessage = null;
    this.serverSuccessMessage = null;
    const message = {
      bobUuid: this.bob.uuid,
      promoCode: this.validCode,
    };
    sendToBobController(message, "/bob_sponsorship/check_promo_code")
      .then((response) => {
        this.bob.setAreWeWaitingOnRails(false);
        if (response.code_type === "bob_gift") {
          if (Array.isArray(response.platform_credits)) {
            this.bob.setPlatformCredits(response.platform_credits);
          }
          if (Array.isArray(response.bob_gifts)) {
            this.bob.setBobGifts(response.bob_gifts);
          }

          const safeBalance = Number(response.safe_balance_in_satoshis);
          const inFlightSatoshis = Number(response.in_flight_satoshis);
          if (
            Number.isFinite(safeBalance) &&
            Number.isFinite(inFlightSatoshis)
          ) {
            this.bob.updateBalance(safeBalance, inFlightSatoshis);
          }

          if (response.success === false) {
            this.setServerError(response.message);
            return;
          }

          this.setServerSuccess(response.message || "Gift card redeemed!");
          return;
        }
        if (response.success === false) {
          this.setServerError(response.message);
          return;
        }
        if (!TIKTOK_SPONSORSHIPS_ENABLED) {
          this.setServerError("Incorrect promo code.");
          return;
        }
        this.bob.setSponsorships(response.sponsorship);
        this.bob.setOpenModal(BOB_MODALS.SPONSORSHIP);
      })
      .catch((error) => {
        this.bob.setAreWeWaitingOnRails(false);
        console.log(error);
      });
  });

  @modelAction
  setServerError(message: string) {
    this.serverErrorMessage = message;
    this.serverSuccessMessage = null;
  }

  @modelAction
  setServerSuccess(message: string) {
    this.serverSuccessMessage = message;
    this.serverErrorMessage = null;
  }

  get bob() {
    return findParent<Bob>(this, (parentNode) => parentNode instanceof Bob);
  }
}

export const BobSponsorshipProposalRef = rootRef<BobSponsorshipProposal>(
  "exrails/BobSponsorshipProposalRef",
  {
    onResolvedValueChange(ref, newThing, oldThing) {
      if (newThing && !oldThing) {
        detach(ref);
      }
    },
  },
);
